import { useEffect, useRef } from "react";

/**
 * Drag/swipe orizzontale con intent detection:
 * - se il gesto è orizzontale -> drag row
 * - se è verticale -> lascia scorrere il body (niente preventDefault)
 */
export default function useSwipeScroll(
  ref,
  { friction = 1, enableWheelX = false, threshold = 6 } = {}
) {
  const state = useRef({
    isDown: false,
    isDragging: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onPointerDown = (e) => {
      // solo tasto primario / touch
      if (e.button !== undefined && e.button !== 0) return;

      state.current.isDown = true;
      state.current.isDragging = false;

      const pageX = e.pageX ?? (e.touches && e.touches[0]?.pageX) ?? 0;
      const pageY = e.pageY ?? (e.touches && e.touches[0]?.pageY) ?? 0;

      state.current.startX = pageX - el.offsetLeft;
      state.current.startY = pageY;
      state.current.scrollLeft = el.scrollLeft;
    };

    const onPointerMove = (e) => {
      if (!state.current.isDown) return;

      const pageX = e.pageX ?? (e.touches && e.touches[0]?.pageX) ?? 0;
      const pageY = e.pageY ?? (e.touches && e.touches[0]?.pageY) ?? 0;

      const x = pageX - el.offsetLeft;
      const dx = x - state.current.startX;
      const dy = pageY - state.current.startY;

      // se non abbiamo ancora deciso l'intento
      if (!state.current.isDragging) {
        // gesto verticale prevalente -> lascia scorrere il body
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > threshold) {
          // annulla lo stato "down" e NON fare preventDefault
          state.current.isDown = false;
          state.current.isDragging = false;
          return;
        }
        // gesto orizzontale prevalente -> inizia drag
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > threshold) {
          state.current.isDragging = true;
          el.classList.add("dragging");
          // cattura solo quando abbiamo deciso che è drag orizzontale
          if (el.setPointerCapture && e.pointerId) el.setPointerCapture(e.pointerId);
        } else {
          return; // sotto soglia: non fare nulla
        }
      }

      // DRAG attivo
      const walk = dx * friction;
      el.scrollLeft = state.current.scrollLeft - walk;
      e.preventDefault(); // blocca solo durante il drag orizzontale
    };

    const onPointerUp = (e) => {
      if (!state.current.isDown && !state.current.isDragging) return;
      state.current.isDown = false;
      state.current.isDragging = false;
      el.classList.remove("dragging");
      if (el.releasePointerCapture && e.pointerId) {
        try { el.releasePointerCapture(e.pointerId); } catch {}
      }
    };

    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp, { passive: true });
    window.addEventListener("pointercancel", onPointerUp, { passive: true });
    window.addEventListener("pointerleave", onPointerUp, { passive: true });

    // (opzionale) conversione wheel -> X, disattivata di default
    let onWheel;
    if (enableWheelX) {
      onWheel = (e) => {
        const { deltaX, deltaY, deltaMode, shiftKey } = e;
        const unit = deltaMode === 1 ? 16 : 1; // normalizza step

        // Se c'è input orizzontale nativo, lascia al browser: è più fluido
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          return; // nessun preventDefault: scrolling nativo orizzontale
        }

        // Solo se SHIFT è premuto convertiamo il verticale in orizzontale
        if (!(shiftKey && Math.abs(deltaY) > 0)) {
          return; // scroll verticale del body
        }

        const horiz = deltaY * unit;
        const maxScroll = el.scrollWidth - el.clientWidth;
        const atStart = el.scrollLeft <= 0;
        const atEnd = el.scrollLeft >= maxScroll - 1;
        const goingRight = horiz > 0;

        if ((goingRight && atEnd) || (!goingRight && atStart)) {
          return; // lascia passare al body
        }

        el.scrollLeft += horiz;
        e.preventDefault(); // blocca solo quando convertiamo noi
      };
      el.addEventListener("wheel", onWheel, { passive: false });
    }

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("pointerleave", onPointerUp);
      if (onWheel) el.removeEventListener("wheel", onWheel);
    };
  }, [ref, friction, enableWheelX, threshold]);
}
